import React, { useCallback, useEffect, useState } from 'react';

import { Box, Divider, Flex, Image } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import CommentContainer from 'components/Comment/CommentContainer';
import { QUERY_KEYS } from 'components/RabbitInitiativeSidePanel/datahooks';
import { useInitiativeCommentsData } from 'components/RabbitInitiativeSidePanel/datahooks/useCommentsData';
// eslint-disable-next-line import/no-cycle
import { FullHistoryPanelHeader } from 'components/RabbitOKRSidePanel/Components/SectionHeaders';
import AutomatedCommentHistory from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/History/AudomatedCommentHistory';
import ManualComment from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/History/ManualComment';
import { closeFullHistoryPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import SkeletonLoader from 'components/SkeletonLoader';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { format } from 'date-fns';
import Post from 'models/post';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import NoActivity from 'assets/assetsV1/illustrations/newIllustrations/noActivity.svg';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { findAndReplaceString } from 'constants/helper';

import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

export const EmptyHistoryUI = ({ isObjective, objectivesName, keyResultsName }) => (
  <Box
    display="flex"
    padding="42px 0px 12px 0px"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    gap="4px"
    alignSelf="stretch"
  >
    {/* @ts-expect-error union is too complex */}
    <Image width="150px" alt="No Activity" src={NoActivity} />
    <Box display="flex" padding="24px" flexDirection="column" alignItems="center" gap="16px" alignSelf="stretch">
      <Box display="flex" flexDirection="column" alignItems="center">
        <TextHeader3 bold>There has been no activity here</TextHeader3>
        <Box display="flex" padding="0px 12px 12px 12px" justifyContent="center" alignItems="center" gap="4px">
          <TextBodyMedium>
            No activities have been recorded for this
            {` ${modifyString(isObjective ? objectivesName : keyResultsName, StringModifier.SENTENCE_CASE)}`}. Edit or
            check-in the{' '}
            {` ${modifyString(isObjective ? objectivesName : keyResultsName, StringModifier.SENTENCE_CASE)}`} to record
            an activity.
          </TextBodyMedium>
        </Box>
      </Box>
    </Box>
  </Box>
);

const FullHistoryPanel = () => {
  const dispatch = useDispatch();

  const limit = 10;
  const [offset, setOffset] = useState(0);
  const [commentsData, setCommentsData] = useState([]);
  const { ref: inViewRef, inView } = useInView({
    /* Optional options */
    threshold: 0,
  });
  const { keyResultsName, objectivesName, moduleName } = useOrganisationSettings();

  const secondaryPanelDetailsObject = useSelector(kRDrawerSecondaryPanelSelector);
  const { createdBy, createdTS } = useSelector(kRDrawerSelector);
  const formattedCreatedOnDate = createdTS ? format(new Date(createdTS), 'MMM dd, yyyy') : '';
  const createdByName = createdBy?.metadata?.displayName ?? '';
  const goalCreatedByMessage = `${modifyString(
    moduleName,
    StringModifier.TITLE_CASE
  )} created by ${createdByName} on ${formattedCreatedOnDate}`;

  const { ids = [], type } = secondaryPanelDetailsObject?.details || {};
  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const queryClient = useQueryClient();

  const { data: curApiCallCommentsData = [], isLoading } = useInitiativeCommentsData({
    enabled: true,
    id: ids,
    keepPreviousData: true,
    limit,
    offset,
  });

  useEffect(() => {
    if (curApiCallCommentsData.length === 0) {
      return;
    }

    const newCommentsData = [...commentsData];

    newCommentsData.splice(offset, 0, ...curApiCallCommentsData);
    newCommentsData.length = offset + curApiCallCommentsData.length;

    setCommentsData(newCommentsData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curApiCallCommentsData]);

  const handleBackButtonClick = useCallback(() => {
    dispatch(closeFullHistoryPanel({}));
  }, [dispatch]);

  const handleCommentUpdate = useCallback(
    ({ commentId, comment }) => {
      const commentIndex = commentsData.findIndex((curComment) => curComment.id === commentId);
      const newCommentsData = [...commentsData];
      newCommentsData[commentIndex] = comment;
      setCommentsData(newCommentsData);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.INITIATIVE_COMMENTS, ids],
      });
    },
    [commentsData, ids, queryClient]
  );

  const getEmptyCommentUI = useCallback(() => {
    let content = null;
    if (isLoading) {
      content = <SkeletonLoader />;
    } else if (commentsData.length === 0) {
      content = (
        <EmptyHistoryUI isObjective={isObjective} objectivesName={objectivesName} keyResultsName={keyResultsName} />
      );
    }
    return content;
  }, [commentsData.length, isLoading, isObjective, keyResultsName, objectivesName]);

  useEffect(() => {
    if (inView) {
      setOffset((prevOffset) => prevOffset + limit);
    }
  }, [inView]);

  return (
    //  @ts-expect-error union is too complex
    <Flex backgroundColor="#FFFFFF" h="100%" flexDirection="column">
      <FullHistoryPanelHeader
        onCollapse={() => {
          handleBackButtonClick();
        }}
        goalCreatedByMessage={goalCreatedByMessage}
      />
      {/* @ts-expect-error union is too complex */}
      <Divider opacity={1} borderColor="#CFD7DF" mb="12px" marginLeft="auto" marginRight="auto" width="95%" />
      <Flex flexDir="column" gap="12px" alignItems="flex-start" padding="16px" overflowY="auto">
        {getEmptyCommentUI()}

        {commentsData.length > 0 &&
          commentsData.map((comment, index) => {
            const updatedComment = {
              ...comment,
              text: findAndReplaceString(comment?.text, keyResultsName),
            };
            return (
              <Box
                width="100%"
                sx={{
                  '.history-comment-container': {
                    width: '100%',
                  },
                }}
              >
                {index + 1 === offset + limit ? (
                  <Box ref={inViewRef}>
                    {' '}
                    <CommentContainer
                      placeholder="Share suggestion or feedback"
                      key={`${updatedComment.id}-taskComment`}
                      {...updatedComment}
                      feedId={ids}
                      feedType="TASK"
                      displayReply={false}
                      styleType="default"
                      employeeId={loggedInEmployee.id}
                      className="history-comment-container"
                      renderDisplayCommentView={({ subType, ...props }) =>
                        subType === Post.SUB_TYPES.automatedComment ? (
                          <AutomatedCommentHistory {...props} />
                        ) : (
                          <ManualComment commentMoreMenuItems={props?.commentMoreMenuItems} {...props} />
                        )
                      }
                      updateFeedComment={handleCommentUpdate}
                    />
                  </Box>
                ) : (
                  <Box>
                    {' '}
                    <CommentContainer
                      placeholder="Share suggestion or feedback"
                      key={`${updatedComment.id}-taskComment`}
                      {...updatedComment}
                      feedId={ids}
                      feedType="TASK"
                      displayReply={false}
                      styleType="default"
                      employeeId={loggedInEmployee.id}
                      className="history-comment-container"
                      renderDisplayCommentView={({ subType, ...props }) =>
                        subType === Post.SUB_TYPES.automatedComment ? (
                          <AutomatedCommentHistory {...props} />
                        ) : (
                          <ManualComment commentMoreMenuItems={props?.commentMoreMenuItems} {...props} />
                        )
                      }
                      updateFeedComment={handleCommentUpdate}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
      </Flex>
    </Flex>
  );
};

export { FullHistoryPanel };
